import { useEffect, useMemo } from 'react'
import { useController, useForm, useWatch } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { clsx } from 'clsx'
import { emptyItem, itemInputIds } from '@/features/consignment-form/item'
import { itemLabels } from '@/features/consignment-form/labels'
import type { Product } from '@/features/consignment-form/products'
import type { Item } from '@/features/consignment-form/schema'
import { MAX_PACKAGES, digitCharacters, weightCharacters } from '@/features/consignment-form/rules'
import { editedItemSchema, uncategorisedItemSchema } from '@/features/consignment-form/schema'
import { t } from '@/lang'
import { Button } from '@/ui/Button'
import { Combobox } from '@/ui/Combobox'
import { FormField } from '@/ui/FormField'
import { CheckIcon } from '@/ui/icons/CheckIcon'
import { CrossIcon } from '@/ui/icons/CrossIcon'
import { TrashBinIcon } from '@/ui/icons/TrashBinIcon'
import { Input } from '@/ui/Input'

const sides = ['lengthCm', 'widthCm', 'heightCm'] as const
const measurements = ['weightKg', ...sides] as const

type Props = {
  products: readonly Product[]
  item?: Item
  onSave: (values: Item) => Promise<void>
  onDraftChange: (values: Item) => void
  onDelete?: () => void
  onCancel?: () => void
}

export function ItemEditor({ products, item, onSave, onDraftChange, onDelete, onCancel }: Props) {
  const resolver = useMemo((): Resolver<Item> => {
    const categorised = zodResolver(editedItemSchema(products))
    const uncategorised = zodResolver(uncategorisedItemSchema)
    return (values, context, options) =>
      (values.category === '' ? uncategorised : categorised)(values, context, options)
  }, [products])
  const { control, formState, getFieldState, getValues, handleSubmit, register, resetField, subscribe, trigger } =
    useForm<Item>({
      mode: 'onTouched',
      defaultValues: item ?? emptyItem,
      resolver,
    })
  const { errors } = formState
  const category = useController({ control, name: 'category' }).field
  const dimensions = useWatch({ control, name: [...sides] })
  const categories = products.map((product) => ({ value: product.code, label: product.title }))
  const selectedProduct = products.find((product) => product.code === category.value)
  const weightRequired = selectedProduct?.weightRequired === true
  // A measurement the category does not demand is still offered: one side filled in asks for the other two.
  const dimensionsRequired =
    selectedProduct !== undefined && (selectedProduct.dimensionsRequired || dimensions.some((value) => value !== ''))
  const save = handleSubmit(onSave)
  const weightField = register('weightKg')

  const changeCategory = (value: string) => {
    const replaced = category.value !== ''
    category.onChange(value)
    // Changing or removing a category starts the row afresh.
    if (replaced) {
      for (const field of ['qty', 'description', ...measurements] as const) resetField(field, { defaultValue: '' })
      return
    }
    // A new category brings new requirements: its empty fields are not at fault until visited or saved.
    for (const field of measurements) resetField(field, { defaultValue: getValues(field) })
    // What was typed is still checked, against the new category's rules.
    const typed = measurements.filter((field) => getValues(field) !== '')
    if (typed.length > 0) void trigger(typed)
  }

  // Whether a side is required depends on the other two, so a change rechecks the sides already marked wrong.
  // Untouched sides stay quiet until visited.
  const registerSide = (field: (typeof sides)[number]) =>
    register(field, {
      onChange: () => {
        const shown = sides.filter((side) => side !== field && getFieldState(side).invalid)
        if (shown.length > 0) void trigger(shown)
      },
    })

  useEffect(
    () => subscribe({ formState: { values: true }, callback: ({ values }) => onDraftChange(values) }),
    [subscribe, onDraftChange]
  )

  return (
    <div
      className={clsx(
        'mt-2.5 grid gap-x-5 gap-y-3 rounded-sm bg-white p-5',
        'md:grid-cols-[150fr_355fr_163fr]',
        'xl:grid-cols-[114fr_370fr_342fr_194fr] xl:gap-x-10 xl:gap-y-5'
      )}
    >
      <FormField
        htmlFor={itemInputIds.qty}
        label={itemLabels.qty}
        required
        error={errors.qty?.message}
        className="max-md:w-37.5 md:col-start-1 md:row-span-2 md:row-start-1"
      >
        <Input
          id={itemInputIds.qty}
          inputMode="numeric"
          maxLength={String(MAX_PACKAGES).length}
          filter={digitCharacters}
          {...register('qty')}
        />
      </FormField>

      <Combobox
        id={itemInputIds.category}
        label={itemLabels.category}
        required
        disabled={products.length === 0}
        items={categories}
        value={category.value}
        onChange={changeCategory}
        onBlur={category.onBlur}
        inputRef={category.ref}
        error={errors.category?.message}
        placeholder={t('item.category-placeholder')}
        className="md:col-start-2 md:row-start-1"
      />

      <FormField
        htmlFor={itemInputIds.description}
        label={itemLabels.description}
        required
        error={errors.description?.message}
        className="md:col-start-2 md:row-start-2"
      >
        <Input
          id={itemInputIds.description}
          placeholder={t('item.description-placeholder')}
          {...register('description')}
        />
      </FormField>

      <FormField
        htmlFor={itemInputIds.weightKg}
        label={itemLabels.weightKg}
        required={weightRequired}
        error={errors.weightKg?.message}
        className="max-md:w-37.5 md:col-start-1 md:row-start-3 xl:col-start-3 xl:row-start-1 xl:w-[calc((100%-2.5rem)/3)]"
      >
        <Input
          id={itemInputIds.weightKg}
          inputMode="decimal"
          filter={weightCharacters}
          {...weightField}
          onBlur={(event) => {
            // The point has to be typed before its digit, so a point left with none after it is dropped on leaving,
            // and one typed with nothing before it gets its zero.
            event.currentTarget.value = event.currentTarget.value.replace(/\.$/, '').replace(/^\./, '0.')
            return weightField.onBlur(event)
          }}
        />
      </FormField>

      <div className="grid grid-cols-3 gap-5 md:col-start-2 md:row-start-3 xl:col-start-3 xl:row-start-2">
        <FormField
          htmlFor={itemInputIds.lengthCm}
          label={itemLabels.lengthCm}
          error={errors.lengthCm?.message}
          required={dimensionsRequired}
        >
          <Input
            id={itemInputIds.lengthCm}
            inputMode="numeric"
            filter={digitCharacters}
            {...registerSide('lengthCm')}
          />
        </FormField>
        <FormField
          htmlFor={itemInputIds.widthCm}
          label={itemLabels.widthCm}
          error={errors.widthCm?.message}
          required={dimensionsRequired}
        >
          <Input id={itemInputIds.widthCm} inputMode="numeric" filter={digitCharacters} {...registerSide('widthCm')} />
        </FormField>
        <FormField
          htmlFor={itemInputIds.heightCm}
          label={itemLabels.heightCm}
          error={errors.heightCm?.message}
          required={dimensionsRequired}
        >
          <Input
            id={itemInputIds.heightCm}
            inputMode="numeric"
            filter={digitCharacters}
            {...registerSide('heightCm')}
          />
        </FormField>
      </div>
      <div
        className={clsx(
          'flex flex-col gap-2.5 *:shrink-0',
          'md:col-start-3 md:row-start-3 md:h-[calc(var(--text-label)*var(--text-label--line-height)+(--spacing(1.5))+(--spacing(9)))] md:justify-end md:gap-6 md:self-start',
          'xl:col-start-4 xl:row-start-2'
        )}
      >
        <Button
          variant="success"
          size="sm"
          icon={<CheckIcon className="w-4 shrink-0" />}
          className="w-full max-md:h-12"
          onClick={save}
          disabled={products.length === 0 || formState.isSubmitting}
        >
          {formState.isSubmitting ? t('item.saving') : t('item.save')}
        </Button>
        {onDelete ? (
          <Button
            variant="danger"
            size="sm"
            icon={<TrashBinIcon className="h-4 shrink-0" />}
            className="w-full max-md:h-12"
            onClick={onDelete}
            disabled={formState.isSubmitting}
          >
            {t('item.delete')}
          </Button>
        ) : null}
        {onCancel ? (
          <Button
            variant="outlined"
            size="sm"
            icon={<CrossIcon className="w-4 shrink-0" />}
            className="w-full max-md:h-12"
            onClick={onCancel}
            disabled={formState.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
